// Phone-extension helpers shared across the encounter/onboarding phone flows.
//
// A phone number that reaches an internal extension is carried through the app
// as a SINGLE dialable string so it threads through every existing
// phone-number field without new plumbing. We keep the extension on the string
// using the RFC 3966 `;ext=123` convention once normalized, and tolerate the
// human shorthands a provider might type (`x123`, `ext 123`, `,123`, `#123`).
// The number is dialed first; the extension is sent afterwards as DTMF tones
// by the SIP layer (see calliope-backend / calliope-telephonic).

// Matches a trailing extension after the dialable number. The separator is
// required so a bare phone number (no `x`/`ext`/`,`/`#`/`;ext=`) never matches
// and is returned untouched.
const PHONE_EXTENSION_RE = /\s*(?:;ext=|ext\.?|x|[,#])\s*(\d+)\s*$/i;

// Split a phone string into its dialable number and a digits-only extension.
// Returns `{number, extension}` with `extension === ''` when none is present.
export const splitPhoneExtension = (
  value: unknown,
): {number: string; extension: string} => {
  const raw = String(value ?? '');
  const match = raw.match(PHONE_EXTENSION_RE);
  if (!match || match.index === undefined) {
    return {number: raw.trim(), extension: ''};
  }
  return {
    number: raw.slice(0, match.index).trim(),
    extension: match[1].replace(/\D/g, ''),
  };
};

// Re-join a number and extension into one display/transport string. Uses the
// ` x<ext>` shorthand for human-facing values; `toE164` rewrites it to the
// canonical `;ext=` form for the wire. Empty extension returns the number.
export const combinePhoneExtension = (
  number: unknown,
  extension: unknown,
): string => {
  const num = String(number ?? '').trim();
  const ext = String(extension ?? '').replace(/\D/g, '');
  if (!ext) return num;
  return num ? `${num} x${ext}` : '';
};
